// CreatePost.js
import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { useLanguage } from './LanguageContext';
import { COUNTRIES } from './config';
import { finalizeEvent } from 'nostr-tools/pure';
import { hexToBytes } from '@noble/hashes/utils';
import AuthPrompt from './AuthPrompt';
import { sanitizeContent, postRateLimiter } from './utils/sanitize';

// Maximum content length to prevent abuse
const MAX_CONTENT_LENGTH = 10000;

const CreatePost = ({ onPostCreated, replyTo = null, enforceGovLink = false, initialContent = '', relays: passedRelays }) => {
  const [content, setContent] = useState(initialContent);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [error, setError] = useState('');
  const { publicKey, privateKey } = useAuth();
  const { country } = useLanguage();
  const countryConfig = COUNTRIES[country];

  const validateGovLink = (content) => {
    // Check for govglance.org
    if (content.includes('govglance.org')) return true;
    
    // Check for government domains
    const domains = countryConfig.govDomains.map(domain => 
      domain.replace('.', '\\.')
    ).join('|');
    const govRegex = new RegExp(`(https?:\\/\\/[^\\s]+(${domains})[^\\s]*)`);
    return govRegex.test(content);
  };

  const handleContentChange = (e) => {
    const newContent = e.target.value;
    // Enforce maximum length
    if (newContent.length <= MAX_CONTENT_LENGTH) {
      setContent(newContent);
      if (error) setError('');
    }
  };

  const handleSubmit = async () => {
    const trimmedContent = content.trim();
    
    if (!trimmedContent) return;
    
    if (!publicKey) {
      setShowAuthPrompt(true);
      return;
    }

    // Rate limiting check
    if (!postRateLimiter.isAllowed(publicKey)) {
      setError('You\'re posting too quickly. Please wait a moment.');
      return;
    }

    if (enforceGovLink && !validateGovLink(trimmedContent)) {
      setError(`Post must contain a valid ${countryConfig.name} government domain link`);
      return;
    }

    // Content length validation
    if (trimmedContent.length > MAX_CONTENT_LENGTH) {
      setError(`Post exceeds maximum length of ${MAX_CONTENT_LENGTH} characters`);
      return;
    }

    setError('');
    setIsSubmitting(true);
    
    try {
      // Sanitize content before posting
      const sanitizedContent = sanitizeContent(trimmedContent);
      
      const event = {
        kind: 1,
        pubkey: publicKey,
        created_at: Math.floor(Date.now() / 1000),
        tags: replyTo ? [["e", replyTo]] : [],
        content: sanitizedContent
      };

      const signedEvent = finalizeEvent(event, hexToBytes(privateKey));

      // Use passed relays or fallback defaults
      const relaysToUse = passedRelays || [
        "wss://relay.primal.net",
        "wss://relay.damus.io",
        "wss://relay.nos.social",
        "wss://sources.nostr1.com"
      ];

      let publishedCount = 0;
      
      for (const relay of relaysToUse) {
        try {
          const ws = new WebSocket(relay);
          await new Promise((resolve) => {
            const timeout = setTimeout(() => {
              ws.close();
              resolve();
            }, 5000);

            ws.onopen = () => {
              ws.send(JSON.stringify(["EVENT", signedEvent]));
            };

            ws.onmessage = (msg) => {
              try {
                const [type, , status] = JSON.parse(msg.data);
                if (type === "OK" && status === true) {
                  publishedCount++;
                  clearTimeout(timeout);
                  ws.close();
                  resolve();
                }
              } catch {
                // Ignore parse errors
              }
            };

            ws.onerror = () => {
              clearTimeout(timeout);
              resolve();
            };
          });
        } catch {
          // Continue to next relay
        }
      }

      if (publishedCount === 0) {
        setError('Failed to publish to any relay. Please try again.');
        return;
      }

      setContent('');
      if (onPostCreated) {
        onPostCreated(signedEvent);
      }
    } catch (error) {
      console.error('Failed to create post:', error);
      setError('Failed to create post. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const remainingChars = MAX_CONTENT_LENGTH - content.length;

  const postId = `post-input-${replyTo || 'new'}`;
  
  return (
    <>
      <div 
        className={`create-post ${replyTo ? 'reply-form' : ''}`}
        role="form"
        aria-label={replyTo ? 'Reply to post' : 'Create new post'}
      >
        <label htmlFor={postId} className="visually-hidden">
          {replyTo ? 'Write your reply' : 'Write your post'}
        </label>
        <textarea
          id={postId}
          value={content}
          onChange={handleContentChange}
          placeholder={
            enforceGovLink 
              ? `Write a post and source a ${countryConfig.name} government site...` 
              : replyTo 
                ? "Write your reply..." 
                : "What's on your mind?"
          }
          className="post-input"
          maxLength={MAX_CONTENT_LENGTH}
          aria-describedby={`${postId}-chars ${error ? `${postId}-error` : ''}`}
          aria-invalid={error ? "true" : "false"}
        />
        {error && (
          <div 
            id={`${postId}-error`}
            className="post-error" 
            role="alert" 
            aria-live="polite"
          >
            {error}
          </div>
        )}
        <div className="post-actions">
          <span 
            id={`${postId}-chars`}
            style={{ 
              fontSize: '0.75rem', 
              color: remainingChars < 100 ? 'var(--error)' : 'var(--text-secondary)',
              marginRight: '1rem',
              visibility: remainingChars < 500 ? 'visible' : 'hidden'
            }}
            aria-live="polite"
            aria-atomic="true"
          >
            {remainingChars} characters remaining
          </span>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!content.trim() || isSubmitting}
            className="post-button"
            aria-busy={isSubmitting}
          >
            {isSubmitting ? 'Posting...' : replyTo ? 'Reply' : 'Post'}
          </button>
        </div>
      </div>

      {showAuthPrompt && (
        <AuthPrompt onClose={() => setShowAuthPrompt(false)} />
      )}
    </>
  );
};

export default CreatePost;
